<script setup lang="ts">
import { useStore } from '@/stores/main';
import { useIonRouter } from '@ionic/vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useStore();
const router = useIonRouter();

defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const handleContinue = () => {
  store.updateLastActivity();
  emit('close');
};

const handleLogout = async () => {
  emit('close');
  await store.logout();
  router.replace('/login');
};
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div 
        v-if="isOpen" 
        class="inactivity-overlay"
      >
        <div class="inactivity-modal">
          <!-- Message -->
          <h2 class="modal-title">{{ t('settings.inactivity.title') }}</h2>
          <p class="modal-message">{{ t('settings.inactivity.message') }}</p>

          <!-- Buttons matching BButton style -->
          <div class="modal-buttons">
            <button 
              class="btn btn-secondary"
              @click="handleLogout"
            >
              {{ t('settings.inactivity.logout') }}
            </button>
            <button 
              class="btn btn-primary"
              @click="handleContinue"
            >
              {{ t('settings.inactivity.continue') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.inactivity-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249, 252, 242, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 1rem;
}

.inactivity-modal {
  background: #f9fcf2;
  border-radius: 1.25rem;
  padding: 2rem 1.5rem;
  max-width: 340px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-title {
  font-family: inherit;
  font-size: 1.375rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.75rem;
  letter-spacing: -0.01em;
}

.modal-message {
  font-family: inherit;
  font-size: 1rem;
  color: #4b5563;
  line-height: 1.6;
  margin: 0 0 1.75rem;
}

.modal-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Button styles matching BButton component */
.btn {
  width: 100%;
  padding: 0.625rem 1.375rem;
  font-size: 1.125rem;
  font-weight: 700;
  border: none;
  border-radius: 1.875rem;
  cursor: pointer;
  letter-spacing: -0.01em;
  font-feature-settings: 'calt' off;
  transition: transform 0.2s ease, opacity 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.btn:active {
  transform: scale(0.98);
}

/* Primary button - matches bg-primary-dark text-white */
.btn-primary {
  background-color: #8d3e79;
  color: white;
}

.btn-primary:hover {
  opacity: 0.9;
}

/* Secondary button - matches bg-primary-lighter border-purple-darker */
.btn-secondary {
  background-color: #f3e8f0;
  color: #8d3e79;
  border: 2px solid #8d3e79;
}

.btn-secondary:hover {
  background-color: #e8d5e3;
}

/* Tablet and Desktop */
@media (min-width: 640px) {
  .inactivity-modal {
    padding: 2.5rem 2rem;
    max-width: 420px;
  }

  .modal-title {
    font-size: 1.5rem;
  }

  .modal-message {
    font-size: 1.0625rem;
  }

  .modal-buttons {
    flex-direction: row;
    gap: 1rem;
  }

  .btn {
    flex: 1;
  }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .inactivity-modal,
.fade-leave-active .inactivity-modal {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.fade-enter-from .inactivity-modal,
.fade-leave-to .inactivity-modal {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}
</style>
